import { cn } from '@h2hspark/ui';
import { ArrowLeft } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

/**
 * **A member page's way back — on the desktop only, since Sumit's ruling of 2026-09-11.**
 *
 * ## Why every back link in the member app is this component
 *
 * Sumit, reviewing #529 on a phone: *"the header disappears on event detail / matches page."* The
 * phone's brand bar was drawn on the four tab screens and nowhere else, because a sub-page carried
 * its own back control and two headers on a 375px screen is one too many. He wants the brand
 * everywhere, so the resolution is the other way round: **the bar is on every screen, and it carries
 * the back control** — `phone-top-bar.tsx`, a 38px round button at the left with the logotype
 * centred, which is what `Member App.dc.html` draws for sub-page chrome.
 *
 * That leaves the page's own link as a second way to do the same thing, six pixels below the first.
 * So it is drawn from `md` and not below it, and **this is the only file that says so** — which is
 * what lets `responsive-behaviour.ts` (`PLT-008` AC-3) hold one entry for the change rather than
 * one per page. Thirteen copies of `hidden md:inline-flex` would be thirteen places for it to drift.
 *
 * **Nothing becomes unreachable.** Above `md` the link is the way back; below it the bar's button
 * is, and it goes to the same place — this component's own `to`, so the two cannot point at
 * different destinations. That is the same shape as the navigation's own entry in the registry:
 * one capability, two places for a hand to find it.
 *
 * ## Two treatments, because the pages have two
 *
 * `inline` is the prototype's back link (§3.6): a leading `arrow-left` at 17px, the label in
 * `font-brand 700/var(--text-sm)`, in `--text-muted`. `floating` is the round 38px button three
 * screens lay over a full-bleed hero — the attendee profile, the match detail and the organizer
 * profile — where an inline link would sit on top of an image.
 */
export type PageBackVariant = 'inline' | 'floating';

/**
 * The class that keeps a page's own back control off the phone.
 *
 * Exported so `responsive-behaviour.ts` can name it and its test can find it, rather than the
 * string being asserted against a copy of itself.
 */
export const PAGE_BACK_DESKTOP_ONLY_CLASS = 'hidden md:inline-flex';

export function PageBackLink({
  to,
  variant = 'inline',
  className,
  children,
}: {
  /** Where back is. The phone bar's button is handed the same value by the page's route. */
  readonly to: string;
  readonly variant?: PageBackVariant;
  readonly className?: string;
  /** The label. Absent on the floating variant, which is a glyph and an accessible name. */
  readonly children?: React.ReactNode;
}): React.ReactElement {
  if (variant === 'floating') {
    return (
      <Link
        to={to}
        aria-label={typeof children === 'string' ? children : 'Back'}
        className={cn(
          PAGE_BACK_DESKTOP_ONLY_CLASS,
          'absolute left-4 top-4 h-[38px] w-[38px] items-center justify-center rounded-pill bg-card/90 text-brand shadow-sm',
          className,
        )}
      >
        <ArrowLeft className="h-5 w-5" aria-hidden />
      </Link>
    );
  }
  return (
    <Link
      to={to}
      className={cn(
        PAGE_BACK_DESKTOP_ONLY_CLASS,
        'items-center gap-1.5 font-brand text-sm font-bold text-muted-foreground hover:text-foreground',
        className,
      )}
    >
      <ArrowLeft className="h-[17px] w-[17px]" aria-hidden />
      {children}
    </Link>
  );
}
